import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import MainA from "../../layout/Main Admin";
import Loader from "../../components/atoms/Loader";
import Table from "../../components/atoms/Table";
import Button from "../../components/atoms/Button";
import Modal from '../../components/atoms/Modal';
import SearchableDropdown from "../../components/atoms/SearchableDropdown";
import { getRequest } from "../../utils/axios";
import { NotificationManager } from "react-notifications";

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileExcel, faTrash } from '@fortawesome/free-solid-svg-icons';

import moment from "moment";
import * as XLSX from "xlsx";

import "./styles.scss";

const FormResponses = () => {
  const navigate = useNavigate();
  const { eventId } = useParams();
  const [loading, setLoading] = useState(true);
  const [responses, setResponses] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [previewUrl, setPreviewUrl] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [groups, setGroups] = useState([]);
  const [events, setEvents] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [emailQuery, setEmailQuery] = useState("");

  const imageUrl = "https://dynamicproductstorage.blob.core.windows.net/form/";

  useEffect(() => {
    fetchResponses();
    getGroups();
    getEvents();
  }, []);

  useEffect(() => {
    if (eventId && events.length > 0) {
      const matchedEvent = events.find(
        (e) => String(e.pageId) === String(eventId)
      );

      if (matchedEvent) {
        setSelectedEvent({
          label: matchedEvent.pageName || matchedEvent.pageTitle,
          value: matchedEvent.pageId,
        });
      }
    } else {
      setSelectedEvent(null);
    }
  }, [eventId, events]);

  const fetchResponses = async () => {
    setLoading(true);
    try {
      const resp = await getRequest(`FormResponse/GetFormResponses`);
      setResponses(Array.isArray(resp.data) ? resp.data : []);
    } catch (err) {
      console.error("Error fetching form responses", err);
      NotificationManager.error("Failed to load form responses");
    } finally {
      setLoading(false);
    }
  };

  const getGroups = async () => {
    try {
      const resp = await getRequest(`Group/GetGroups`);
      setGroups(Array.isArray(resp.data) ? resp.data : []);
    } catch (err) {
      console.error("Error fetching form groups", err);
      NotificationManager.error("Failed to load form groups");
    } finally {
      setLoading(false);
    }
  };

  // client-side filtering
  const filteredResponses = (Array.isArray(responses) ? responses : []).filter((r) => {
    const q = String(emailQuery || "").trim().toLowerCase();
    // group match
    const groupMatch = !selectedGroup || selectedGroup.value === null || (
      (r.groupId !== undefined && r.groupId === selectedGroup.value) ||
      (r.formGroupId !== undefined && r.formGroupId === selectedGroup.value) ||
      (r.group && (r.group.groupId === selectedGroup.value || r.group.groupId == selectedGroup.value)) ||
      (r.groupId == selectedGroup.value)
    );

    // event match
    const eventMatch = !selectedEvent || selectedEvent.value === null || (
      (r.eventId !== undefined && r.eventId === selectedEvent.value) ||
      (r.page && (r.page.pageId === selectedEvent.value || r.page.pageId == selectedEvent.value)) ||
      (r.eventId == selectedEvent.value) ||
      (r.pageName && String(r.pageName) === String(selectedEvent.label))
    );

    // email/text search: match submittedBy or any already-loaded response values
    const emailMatch = !q || (
      (r.submittedBy && String(r.submittedBy).toLowerCase().includes(q))
    );

    return groupMatch && eventMatch && emailMatch;
  });

  const getEvents = async () => {
    try {
      const resp = await getRequest(`Page/GetBasicPagesByCategoryTypeName/events`);
      setEvents(Array.isArray(resp.data) ? resp.data : []);
    } catch (err) {
      console.error("Error fetching events", err);
      NotificationManager.error("Failed to load events");
    } finally {
      setLoading(false);
    }
  };

  const toggleRow = async (formResponseId) => {
    if (!formResponseId) return;

    const cur = expanded[formResponseId];

    if (cur && cur.open) {
      setExpanded((prev) => ({
        ...prev,
        [formResponseId]: { ...cur, open: false },
      }));
      return;
    }

    const closeAll = (state) => {
      const copy = { ...state };
      Object.keys(copy).forEach((k) => {
        copy[k] = { ...copy[k], open: false };
      });
      return copy;
    };

    if (cur && Array.isArray(cur.values) && cur.values.length > 0) {
      setExpanded((prev) => {
        const closed = closeAll(prev);
        return { ...closed, [formResponseId]: { ...cur, open: true } };
      });
      return;
    }

    setExpanded((prev) => {
      const closed = closeAll(prev);
      return {
        ...closed,
        [formResponseId]: { open: true, loading: true, values: [] },
      };
    });

    try {
      const resp = await getRequest(
        `FormResponseValue/GetByFormResponse/${formResponseId}`,
      );
      const values = Array.isArray(resp.data) ? resp.data : [];
      setExpanded((prev) => {
        const closed = closeAll(prev);
        return {
          ...closed,
          [formResponseId]: { open: true, loading: false, values },
        };
      });
    } catch (err) {
      console.error("Error fetching response values", err);
      setExpanded((prev) => {
        const closed = closeAll(prev);
        return {
          ...closed,
          [formResponseId]: { open: true, loading: false, values: [] },
        };
      });
      NotificationManager.error("Failed to load response values");
    }
  };

  const handleDeleteClick = (resp) => {
    setDeleteTarget(resp);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setLoading(true);
    try {
      const resp = await getRequest(`FormResponse/Delete/${deleteTarget.formResponseId}`);
      // assuming API returns updated list or success; refetch to update
      await fetchResponses();
      NotificationManager.success('Response deleted successfully');
    } catch (err) {
      console.error('Error deleting response', err);
      NotificationManager.error('Failed to delete response');
    } finally {
      setLoading(false);
      setDeleteConfirmOpen(false);
      setDeleteTarget(null);
    }
  };

  const handleCloseDeleteModal = () => {
    setDeleteConfirmOpen(false);
    setDeleteTarget(null);
  };

  const exportToExcel = async () => {
    try {
      setLoading(true);
      const responsesToExport = Array.isArray(filteredResponses) ? filteredResponses : [];

      // fetch values for each response (use existing expanded values if present)
      const promises = responsesToExport.map(async (r) => {
        try {
          const resp = await getRequest(`FormResponseValue/GetByFormResponse/${r.formResponseId}`);
          return { formResponseId: r.formResponseId, values: Array.isArray(resp.data) ? resp.data : [], meta: r };
        } catch (err) {
          return { formResponseId: r.formResponseId, values: [], meta: r };
        }
      });

      const all = await Promise.all(promises);

      // Build a single sheet: one row per response, columns for metadata + one column per field
      const fieldOrder = [];
      const fieldSet = new Set();

      all.forEach((item) => {
        (item.values || []).forEach((v) => {
          const name = (v.formField && (v.formField.fieldName || v.formField.displayName)) || `Field ${v.formFieldId || ""}`;
          if (!fieldSet.has(name)) {
            fieldSet.add(name);
            fieldOrder.push(name);
          }
        });
      });

      const rows = all.map((item) => {
        const meta = item.meta || {};
        const row = {
          SubmittedBy: meta.submittedBy || "",
          Event: (events.find((e) => e.pageId === meta.eventId)?.pageName) || "",
          Group: meta.groupId ? (groups.find((g) => g.groupId === meta.groupId)?.groupName || "SOLO") : "SOLO",
          SubmittedOn: meta.submittedDate ? moment(meta.submittedDate).format("MMMM DD, YYYY hh:mm A") : "",
        };

        (item.values || []).forEach((v) => {
          const name = (v.formField && (v.formField.fieldName || v.formField.displayName)) || `Field ${v.formFieldId || ""}`;
          row[name] = v.value ?? "";
        });

        // ensure all fields exist on the row (so sheet has consistent columns)
        fieldOrder.forEach((f) => {
          if (!(f in row)) row[f] = "";
        });

        return row;
      });

      const header = ["SubmittedBy", "Event", "Group", "SubmittedOn", ...fieldOrder];

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(rows, { header });
      XLSX.utils.book_append_sheet(wb, ws, "Responses");

      XLSX.writeFile(wb, `form-responses-${moment().format("YYYYMMDD_HHmmss")}.xlsx`);
    } catch (err) {
      console.error("Export error", err);
      NotificationManager.error("Failed to export responses");
    } finally {
      setLoading(false);
    }
  };

  return (
    <MainA className="b-form-responses-wrapper">
      <Loader loading={loading} />
      <div className="wrapper">
        <div className="head">
          <div className="title">
            <h4>Form Responses</h4>
          </div>
          <div className="filters-row">
            <div className="filter-input">
              <label className="filter-label">Search: </label>
              <input
                type="text"
                className="email-search"
                placeholder="Search"
                value={emailQuery}
                onChange={(e) => setEmailQuery(e.target.value)}
              />
            </div>
            <SearchableDropdown
                options={[{ label: "All Groups", value: null }, ...(Array.isArray(groups) ? groups.map((g) => ({ label: g.groupName || "Untitled Group", value: g.groupId })) : [])]}
                label="Group: "
                value={selectedGroup}
                placeholder="All Groups"
                className="filter-dropdown group-dropdown"
                onSelect={(opt) => setSelectedGroup(opt && opt.value !== null ? opt : null)}
            />

            <SearchableDropdown
                options={[{ label: "All Events", value: null }, ...(Array.isArray(events) ? events.map((e) => ({ label: e.pageName || e.pageTitle || "Untitled Event", value: e.pageId })) : [])]}
                label="Event: "
                value={selectedEvent}
                placeholder="All Events"
                className="filter-dropdown event-dropdown"
                onSelect={(opt) => setSelectedEvent(opt && opt.value !== null ? opt : null)}
            />
            
            <FontAwesomeIcon
              onClick={exportToExcel}
              icon={faFileExcel}
              title='Export Responses to Excel'
              style={{ color: '#1d6f42' }}
              className='excel-btn'
            />
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
              aria-label="Go back"
            >
              Back
            </Button>
          </div>
        </div>

        <div className="cards">
          <div className="card">
            <div className="card-title">Total Responses</div>
            <div className="card-value">
              {Array.isArray(filteredResponses) ? filteredResponses.length : 0}
            </div>
          </div>
        </div>

        {!loading && (
          <div className="responses-table">
            {Array.isArray(filteredResponses) && filteredResponses.length > 0 ? (
              <Table pagination={true} pageSize={10} totalRows={filteredResponses.length}>
                <thead>
                  <tr>
                    <th />
                    <th>Participant</th>
                    <th>Event</th>
                    <th>Group</th>
                    <th>Submitted On</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredResponses
                    .sort(
                      (a, b) =>
                        new Date(b.submittedDate).getTime() -
                        new Date(a.submittedDate).getTime(),
                    )
                    .map((r) => (
                      <React.Fragment key={r.formResponseId}>
                        <tr
                          className="response-row"
                          onClick={() => toggleRow(r.formResponseId)}
                        >
                          <td className="toggle-cell">
                            <span className="toggle-icon" aria-hidden>
                              {expanded[r.formResponseId] &&
                              expanded[r.formResponseId].open
                                ? "−"
                                : "+"}
                            </span>
                          </td>
                          <td>{r.submittedBy || "-"}</td>
                          <td>{events.find((e) => e.pageId === r.eventId)?.pageName || "-"}</td>
                          <td>{r.groupId ? groups.find((g) => g.groupId === r.groupId)?.groupName || "-" : "SOLO"}</td>
                          <td>
                            {r.submittedDate
                              ? moment(r.submittedDate).format(
                                  "MMMM DD, YYYY hh:mm A",
                                )
                              : "-"}
                          </td>
                          <td>
                            <Button onClick={(e) => { e.stopPropagation(); handleDeleteClick(r); }} className="delete-btn">
                              <FontAwesomeIcon icon={faTrash} />
                            </Button>
                          </td>
                        </tr>
                        {expanded[r.formResponseId] &&
                          expanded[r.formResponseId].open && (
                            <tr className="expanded-row">
                              <td colSpan={6}>
                                {expanded[r.formResponseId].loading ? (
                                  <div>Loading response...</div>
                                ) : (
                                  <div className="expanded-content">
                                    {/* main values table on the left */}
                                    <div className="expanded-main">
                                      {Array.isArray(
                                        expanded[r.formResponseId].values,
                                      ) &&
                                      expanded[r.formResponseId].values.length >
                                        0 ? (
                                        <table className="v-small-table">
                                          <thead>
                                            <tr>
                                              <th>Field</th>
                                              <th>Value</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {expanded[
                                              r.formResponseId
                                            ].values.map((v) => (
                                              <tr
                                                key={
                                                  v.formResponseValueId ||
                                                  v.formFieldId
                                                }
                                              >
                                                <td>
                                                  {(v.formField &&
                                                    (v.formField.fieldName ||
                                                      v.formField
                                                        .displayName)) ||
                                                    `Field ${v.formFieldId || ""}`}
                                                </td>
                                                <td>{v.value ?? "-"}</td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      ) : (
                                        <div>
                                          No values found for this response.
                                        </div>
                                      )}
                                    </div>
                                    {/* right side: show uploaded payment image if present */}
                                    <div className="expanded-side">
                                      {(() => {
                                        const vals =
                                          expanded[r.formResponseId].values ||
                                          [];
                                        const imgVal = vals.find((v) => {
                                          if (!v || !v.value) return false;
                                          const s = String(v.value);
                                          return /\.(jpg|jpeg|png|gif|webp|bmp|svg|tiff|tif|ico|avif|heic|heif|jfif)(\?|$)/i.test(
                                            s,
                                          );
                                        });
                                        const url = imgVal
                                          ? imgVal.value
                                          : null;
                                        return url ? (
                                          <div className="payment-image-wrap">
                                            <img
                                              src={`${imageUrl}${url}`}
                                              alt="payment upload"
                                              onClick={() =>
                                                setPreviewUrl(
                                                  `${imageUrl}${url}`,
                                                )
                                              }
                                              style={{ cursor: "zoom-in" }}
                                            />
                                          </div>
                                        ) : (
                                          <div className="no-image">
                                            No payment image
                                          </div>
                                        );
                                      })()}
                                    </div>
                                  </div>
                                )}
                              </td>
                            </tr>
                          )}
                      </React.Fragment>
                    ))}
                </tbody>
              </Table>
            ) : (
              <div className="no-data">
                No responses found.
              </div>
            )}

            {/* image preview modal (single global overlay) */}
            {previewUrl && (
              <div
                className="image-preview-overlay"
                onClick={() => setPreviewUrl(null)}
              >
                <div
                  className="image-preview-inner"
                  onClick={(e) => e.stopPropagation()}
                >
                  <img src={previewUrl} alt="preview" />
                </div>
              </div>
            )}
            {/* delete confirmation modal */}
            {deleteConfirmOpen && (
              <Modal isOpen={deleteConfirmOpen} closeModal={handleCloseDeleteModal} className="delete-popup">
                <div className='wrapper'>
                  <h4>Are you sure you want to delete this response?</h4>
                  <div className='v-modal-button-wrapper'>
                    <Button onClick={handleDeleteConfirm}>Yes, Remove it</Button>
                    <Button onClick={handleCloseDeleteModal} type="primary">Cancel</Button>
                  </div>
                </div>
              </Modal>
            )}
          </div>
        )}
      </div>
    </MainA>
  );
};

export default FormResponses;
